/* eslint-disable no-nested-ternary -- ok */
import { PageHeading } from '@/components/base/page-heading';
import { cn } from '@/utils';
import { AlertTriangle, Shield, Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';
import { PriceTiers, StepsType, getTiers } from '../configuration';
import {
  EnterpriseRadioGroup,
  EnterpriseRadioGroupItem,
} from './enterprise-group';

// Type definitions for better type safety
interface AccountPriceDecorationProps {
  price: number;
  finalPrice: number;
  oldPrice: number;
  currency: string;
}

interface CompactBadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'info' | 'premium';
  className?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface SelectAccountsizeProps {
  initialState?: string;
  onChange: (value: number) => Promise<void>;
  currency: string;
  country?: string;
  hardBalance?: number | string;
  finalPrice?: number;
  oldPrice?: number;
  steps?: StepsType | undefined | null;
  formSubmitted?: boolean;
  plan: string;
  loading: boolean;
}

interface AccountBadge {
  label: string;
  variant: 'info' | 'premium';
  icon: React.ComponentType<{ className?: string }>;
}

// Translation function type
type TranslationFunction = (key: string) => string;

export const getPrice = (
  balance: number,
  steps: StepsType | undefined | null = StepsType.TwoSteps,
): number => {
  if (
    steps !== null &&
    Object.prototype.hasOwnProperty.call(PriceTiers, steps)
  ) {
    return (
      PriceTiers[steps].find((price) => price.balance === balance)?.price ?? 0
    );
  }
  return 0;
};

// Enhanced price decoration component
export function AccountPriceDecoration({
  price,
  finalPrice,
  oldPrice,
  currency,
}: AccountPriceDecorationProps): JSX.Element {
  const intl = useIntl();
  const finalPricing = finalPrice !== price ? finalPrice : price;
  const hasDiscount = oldPrice > finalPricing;

  return (
    <div className="text-right">
      {hasDiscount && (
        <div className="text-xs font-medium text-red-500 line-through dark:text-red-400">
          {intl.formatNumber(Number(oldPrice ?? 0), {
            style: 'currency',
            currency,
            maximumFractionDigits: 2,
          })}
        </div>
      )}
      <div
        className={cn(
          'font-bold',
          hasDiscount
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-gray-900 dark:text-white',
        )}
      >
        {intl.formatNumber(finalPricing, {
          style: 'currency',
          currency,
          maximumFractionDigits: 2,
        })}
      </div>
    </div>
  );
}

// Compact badge for popular accounts
function CompactBadge({
  children,
  variant = 'default',
  className = '',
  icon: Icon,
}: CompactBadgeProps): JSX.Element {
  const variants = {
    default: 'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300',
    info: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300',
    premium:
      'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-950/40 dark:to-pink-950/40 text-purple-700 dark:text-purple-300',
  };

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
        variants[variant],
        className,
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </div>
  );
}

// Get appropriate badge for certain account sizes
const getAccountBadge = (
  balance: number,
  t: TranslationFunction,
): AccountBadge | null => {
  if (balance === 50000) {
    return {
      label: t('selectAccountSize.popular'),
      variant: 'info' as const,
      icon: Star,
    };
  }
  if (balance >= 100000) {
    return {
      label: t('selectAccountSize.pro'),
      variant: 'premium' as const,
      icon: Shield,
    };
  }
  return null;
};

export function SelectAccountSize({
  initialState,
  onChange = () => Promise.resolve(),
  currency = 'USD',
  finalPrice,
  oldPrice,
  steps,
  formSubmitted = false,
  plan,
  loading = false,
}: SelectAccountsizeProps): JSX.Element {
  const [selectedItem, setSelectedItem] = useState(Number(initialState ?? 0));
  const intl = useIntl();
  const key = initialState;
  const { t } = useTranslation();

  async function handleOnChange(value: number): Promise<void> {
    await onChange(value);
    setSelectedItem(value);
  }

  useEffect(() => {
    setSelectedItem(Number(initialState ?? 0));
  }, [initialState]);

  return (
    <div
      key={key}
      id="select-account-size-section"
      className={cn({
        'rounded-xl border-2 border-red-400/50 bg-red-50/50 p-4 dark:bg-red-950/20':
          formSubmitted && !selectedItem,
      })}
    >
      <PageHeading
        size="xxlarge"
        subheading={t('selectAccountSize.subheading')}
      >
        {t('selectAccountSize.heading')}
      </PageHeading>

      {/* Form Validation Message */}
      {formSubmitted && !selectedItem && (
        <div className="mt-4 flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4" />
          <span>{t('selectAccountSize.validationError')}</span>
        </div>
      )}

      <EnterpriseRadioGroup
        onValueChange={(v: string) => {
          onChange(Number(v));
        }}
        defaultValue={initialState}
        className="mb-8 mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
      >
        {getTiers(plan as StepsType).map((item) => {
          const currentPrice = getPrice(item, steps);
          const displayFinalPrice = loading
            ? currentPrice
            : selectedItem === item
              ? Number(finalPrice)
              : currentPrice;
          const displayOldPrice = loading
            ? currentPrice
            : selectedItem === item
              ? Number(oldPrice)
              : currentPrice;

          const badge = getAccountBadge(item, t);

          return (
            <EnterpriseRadioGroupItem
              onClick={async () => {
                await handleOnChange(item);
              }}
              key={item}
              value={String(item)}
              data-testid={String(item)}
            >
              {/* Badge for popular/pro accounts */}
              {badge && (
                <div className="absolute left-3 top-3">
                  <CompactBadge variant={badge.variant} icon={badge.icon}>
                    {badge.label}
                  </CompactBadge>
                </div>
              )}

              {/* Price - Top Right with Blue Label */}
              <div className="absolute right-12 top-3">
                <div className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                  <AccountPriceDecoration
                    price={currentPrice}
                    currency={currency}
                    finalPrice={displayFinalPrice}
                    oldPrice={displayOldPrice}
                  />
                </div>
              </div>

              {/* Main Content - Dense like original */}
              <div className="flex w-full items-center justify-between gap-5">
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {intl.formatNumber(item, {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  })}
                </span>

                {/* Loading State */}
                {loading && (
                  <div className="h-3 w-3 animate-spin rounded-full border border-blue-600 border-t-transparent" />
                )}
              </div>
            </EnterpriseRadioGroupItem>
          );
        })}
      </EnterpriseRadioGroup>
    </div>
  );
}
